import React, { useState } from 'react'

import AlternativeSetupsDialog from './AlternativeSetupsDialog'
import UseWebimapServerDialog from './UseWebimapServerDialog'
import Button from '../../Button'
import useDialog from '../../../hooks/dialog/useDialog'
import useTranslationFunction from '../../../hooks/useTranslationFunction'
import { DialogBody, DialogContent, DialogFooter, DialogHeader } from '../../Dialog'
import { ClickableLink } from '../../helpers/ClickableLink'
import { instanceAnalyticsUi } from '../../../utils/slothfulInstanceConfig'

import styles from './styles.module.scss'

type Props = {
  onExitWelcomeScreen: () => Promise<void>
  onNextStep: () => void
  onUnSelectAccount: () => Promise<void>
  onClose: () => Promise<void>
  selectedAccountId: number
  hasConfiguredAccounts: boolean
}

/**
 * Sub component of WelcomeScreen shows the choice
 * to create a new account or to use an existing one
 */

export default function OnboardingScreen(props: Props) {
  const tx = useTranslationFunction()
  const { openDialog } = useDialog()

  // slothfulchat: opt-out checkbox for the anonymous usage statistics — only
  // rendered on instances built with analytics (undefined on self-hosted ones)
  const analyticsUi = instanceAnalyticsUi()
  const [shareStats, setShareStats] = useState(() => analyticsUi?.enabled() ?? false)
  const onToggleStats = (event: React.MouseEvent<HTMLInputElement>) => {
    if (!analyticsUi) {
      return
    }
    // don't toggle consent here — the dialog's Opt out / Accept buttons are
    // the only place that changes it; re-sync the checkbox once it closes
    // (Esc = no change). preventDefault on click reliably blocks the toggle.
    event.preventDefault()
    analyticsUi.showInfo().then(() => setShareStats(analyticsUi.enabled()))
  }

  const onAlreadyHaveAccount = () => {
    openDialog(AlternativeSetupsDialog, {
      selectedAccountId: props.selectedAccountId,
    })
  }

  const onUseWebimapServer = () => {
    openDialog(UseWebimapServerDialog, {
      selectedAccountId: props.selectedAccountId,
    })
  }

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    props.onNextStep()
  }

  return (
    <>
      <DialogHeader
        onClose={props.hasConfiguredAccounts ? props.onClose : undefined}
        title={
          props.hasConfiguredAccounts
            ? tx('add_account')
            : tx('welcome_desktop')
        }
      />
      <DialogBody>
        <DialogContent>
          <form onSubmit={onSubmit}>
            <div className={styles.welcomeScreenSection}>
              {/* slothfulchat: our app icon, replaces the upstream intro1.png logo */}
              <img
                className={styles.welcomeScreenIcon}
                src='./images/icon-512.png'
                alt='SlothfulChat'
              />
              <p className={styles.welcomeScreenTitle}>SlothfulChat</p>
              <p style={{ textAlign: 'center', margin: '10px 0 0' }}>
                An experimental fork of Delta Chat running fully in your browser,
                with features beyond stock Delta Chat — unofficial, and it may be buggy.
              </p>
            </div>
            <div className={styles.welcomeScreenButtonGroup}>
              <Button
                type='submit'
                className={styles.welcomeScreenButton}
                styling='primary'
                data-testid='create-account-button'
              >
                {tx('onboarding_create_instant_account')}
              </Button>
              <Button
                className={styles.welcomeScreenButton}
                onClick={onAlreadyHaveAccount}
                data-testid='have-account-button'
              >
                {tx('onboarding_alternative_logins')}
              </Button>
              <Button
                className={styles.welcomeScreenButton}
                onClick={onUseWebimapServer}
              >
                madmail server — no bridge needed, extra experimental
              </Button>
            </div>
            <br /> {/* space after buttons */}
          </form>
        </DialogContent>
      </DialogBody>
      <DialogFooter>
        {analyticsUi && (
          <div
            className={styles.welcomeScreenFooterLinks}
            style={{ fontSize: '13px', marginBottom: '4px' }}
          >
            <label>
              <input
                type='checkbox'
                checked={shareStats}
                onClick={onToggleStats}
                readOnly
              />{' '}
              Share anonymous usage statistics
            </label>{' '}
            (
            <ClickableLink href={new URL('privacy.html', document.baseURI).href}>
              learn more
            </ClickableLink>
            )
          </div>
        )}
        <div className={styles.welcomeScreenFooterLinks}>
          <ClickableLink href='https://github.com/experintellia/slothfulchat-web'>
            Source code
          </ClickableLink>
          {' · '}
          <ClickableLink href={new URL('imprint.html', document.baseURI).href}>
            Imprint
          </ClickableLink>
          {' · '}
          <ClickableLink href={new URL('privacy.html', document.baseURI).href}>
            Privacy
          </ClickableLink>
        </div>
      </DialogFooter>
    </>
  )
}
